import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { NotificationType } from '../../shared/enums/notification-type';

@Injectable()
export class NotificationService {
  constructor(
    private translateService: TranslateService,
    private toastrService: ToastrService,
  ) {}

  async show(
    type: NotificationType,
    messageKey: string,
    titleKey?: string,
  ): Promise<void> {
    switch (type) {
      case 'warning':
        await this.showWarning(messageKey, titleKey);
        break;
      case 'info':
        await this.showInfo(messageKey, titleKey);
        break;
      case 'success':
        await this.showSuccess(messageKey, titleKey);
        break;
      case 'error':
        await this.showError(messageKey, titleKey);
        break;
      default:
        await this.showDefault(messageKey, titleKey);
    }
  }

  showRawError(message: string) {
    this.toastrService.error(message);
  }

  private async showWarning(
    messageKey: string,
    titleKey?: string,
  ): Promise<void> {
    const message = await this.getTranslation(messageKey);
    if (!titleKey) {
      this.toastrService.warning(message);
      return;
    }
    const title = await this.getTranslation(titleKey);
    this.toastrService.warning(message, title);
  }

  private async showInfo(messageKey: string, titleKey?: string): Promise<void> {
    const message = await this.getTranslation(messageKey);
    if (!titleKey) {
      this.toastrService.info(message);
      return;
    }
    const title = await this.getTranslation(titleKey);
    this.toastrService.info(message, title);
  }

  private async showError(
    messageKey: string,
    titleKey?: string,
  ): Promise<void> {
    const message = await this.getTranslation(messageKey);
    if (!titleKey) {
      this.toastrService.error(message);
      return;
    }
    const title = await this.getTranslation(titleKey);
    this.toastrService.error(message, title);
  }

  private async showSuccess(
    messageKey: string,
    titleKey?: string,
  ): Promise<void> {
    const message = await this.getTranslation(messageKey);
    if (!titleKey) {
      this.toastrService.success(message);
      return;
    }
    const title = await this.getTranslation(titleKey);
    this.toastrService.success(message, title);
  }

  private async showDefault(
    messageKey: string,
    titleKey?: string,
  ): Promise<void> {
    const message = await this.getTranslation(messageKey);
    if (!titleKey) {
      this.toastrService.show(message);
      return;
    }
    const title = await this.getTranslation(titleKey);
    this.toastrService.show(message, title);
  }

  private async getTranslation(translationKey: string): Promise<string> {
    return await this.translateService.get(translationKey).toPromise();
  }
}
